import { LOCAL_PAGES } from "@/data/localpages";
import { IMG, SITE } from "@/data/content";

// ── Service templates that get crossed with every city ──
const SERVICE_TEMPLATES = {
    diogene: {
        serviceSlug: "nettoyage-syndrome-diogene",
        serviceLabel: "Syndrome de Diogène",
        serviceType: "Nettoyage syndrome de Diogène",
        kicker: (city) => `Syndrome de Diogène · ${city}`,
        title: (city) => ["Nettoyage syndrome de Diogène", `à ${city}`],
        metaTitle: (city) => `Nettoyage syndrome de Diogène à ${city} | IGN Services Alsace`,
        metaDesc: (city, dept) =>
            `Nettoyage extrême et syndrome de Diogène à ${city} (${dept}) : débarras, désencombrement, désinfection, désodorisation. Intervention discrète et sans jugement. Devis confidentiel gratuit.`,
        heroImg: IMG.diogene,
        intro: (city) => [
            `IGN Services Alsace intervient à ${city} avec discrétion, respect et sans jugement dans les logements fortement encombrés, dégradés ou devenus insalubres. Chaque situation étant différente, nous mettons en place une intervention adaptée aux besoins du logement et de ses occupants.`,
            `Que ce soit pour un logement privé, une demande d'un propriétaire, d'un bailleur ou d'un professionnel à ${city}, nous organisons une prise en charge complète : tri, débarras, évacuation, nettoyage approfondi, désinfection et désodorisation.`,
        ],
        chips: ["Intervention discrète", "Sans jugement", "Devis confidentiel", "Prise en charge complète", "Désinfection", "Désodorisation"],
        faq: (city) => [
            { q: `Intervenez-vous pour un syndrome de Diogène à ${city} ?`, a: `Oui, IGN Services Alsace intervient à ${city} et dans toute l'Alsace pour le nettoyage de logements fortement encombrés ou insalubres, avec discrétion et professionnalisme.` },
            { q: `L'intervention est-elle confidentielle à ${city} ?`, a: `Absolument. Les échanges, le devis et l'intervention sont entièrement confidentiels. Nous intervenons avec discrétion à ${city}.` },
            { q: `Combien coûte un nettoyage Diogène à ${city} ?`, a: `Le tarif dépend de la superficie, du niveau d'encombrement et des prestations nécessaires. Un devis personnalisé et confidentiel est établi après évaluation de la situation à ${city}.` },
        ],
        ctaLabel: "Demander un devis confidentiel",
        parentLink: "/nettoyage-syndrome-diogene-strasbourg",
        parentLabel: "Page Diogène principale",
    },
    deces: {
        serviceSlug: "nettoyage-apres-deces",
        serviceLabel: "Nettoyage après décès",
        serviceType: "Nettoyage après décès",
        kicker: (city) => `Nettoyage après décès · ${city}`,
        title: (city) => ["Nettoyage après décès", `à ${city}`],
        metaTitle: (city) => `Nettoyage après décès à ${city} | IGN Services Alsace`,
        metaDesc: (city, dept) =>
            `Nettoyage après décès à ${city} (${dept}) : nettoyage, désinfection, désodorisation et remise en état. Intervention respectueuse et confidentielle. Devis gratuit sous 24h.`,
        heroImg: IMG.deces,
        intro: (city) => [
            `IGN Services Alsace intervient à ${city} avec respect, dignité et discrétion pour le nettoyage, la désinfection et la remise en état des logements après un décès. Nous accompagnons les familles et les mandataires avec professionnalisme et bienveillance.`,
            `À ${city}, nous proposons une prise en charge complète adaptée à chaque situation : retrait des éléments souillés, nettoyage approfondi, désinfection, désodorisation et remise en état du logement.`,
        ],
        chips: ["Respect & dignité", "Intervention discrète", "Accompagnement familial", "Désinfection", "Désodorisation", "Remise en état"],
        faq: (city) => [
            { q: `Intervenez-vous après un décès à ${city} ?`, a: `Oui, IGN Services Alsace intervient à ${city} et dans toute l'Alsace pour le nettoyage, la désinfection et la remise en état après un décès, avec respect et confidentialité.` },
            { q: `Pouvez-vous intervenir rapidement à ${city} ?`, a: `Oui, selon les disponibilités et le niveau d'urgence, nous pouvons organiser une intervention rapide à ${city}. Contactez-nous pour une évaluation confidentielle.` },
            { q: `Qui peut demander l'intervention à ${city} ?`, a: `La demande peut être faite par la famille, un notaire, un bailleur, un syndic ou toute personne mandatée pour la gestion du logement à ${city}.` },
        ],
        ctaLabel: "Demander un devis confidentiel",
        parentLink: "/nettoyage-apres-deces-strasbourg",
        parentLabel: "Page décès principale",
    },
    remise: {
        serviceSlug: "remise-en-etat",
        serviceLabel: "Remise en état",
        serviceType: "Remise en état de locaux",
        kicker: (city) => `Remise en état · ${city}`,
        title: (city) => ["Remise en état", `à ${city}`],
        metaTitle: (city) => `Remise en état à ${city} | IGN Services Alsace`,
        metaDesc: (city, dept) =>
            `Remise en état professionnelle à ${city} (${dept}) : après sinistre, déménagement, travaux ou dégradation. Nettoyage en profondeur, résultat impeccable. Devis gratuit sous 24h.`,
        heroImg: IMG.about,
        intro: (city) => [
            `IGN Services Alsace réalise la remise en état complète de vos locaux à ${city} : après un sinistre, un déménagement, des travaux ou une période d'inoccupation. Notre objectif est simple : redonner de la valeur à vos espaces.`,
            `À ${city}, chaque intervention de remise en état est pensée avec la rigueur d'un audit professionnel. Sols, surfaces, sanitaires, vitrerie — tout est traité méthodiquement pour un résultat irréprochable.`,
        ],
        chips: ["Après travaux", "Après déménagement", "Après sinistre", "Nettoyage en profondeur", "Résultat garanti", "Devis gratuit"],
        faq: (city) => [
            { q: `Faites-vous la remise en état de logements à ${city} ?`, a: `Oui, nous réalisons la remise en état complète de logements, bureaux et locaux professionnels à ${city} après travaux, déménagement, sinistre ou dégradation.` },
            { q: `Sous combien de temps pouvez-vous intervenir à ${city} ?`, a: `Nous répondons sous 24h et organisons l'intervention rapidement selon vos contraintes à ${city}.` },
            { q: `Quel est le tarif d'une remise en état à ${city} ?`, a: `Le tarif dépend de la superficie, de l'état des lieux et des prestations nécessaires. Un devis gratuit et détaillé est établi après évaluation à ${city}.` },
        ],
        ctaLabel: "Obtenir un devis gratuit",
        parentLink: "/#services",
        parentLabel: "Nos services",
    },
    desinfection: {
        serviceSlug: "desinfection",
        serviceLabel: "Désinfection",
        serviceType: "Désinfection professionnelle",
        kicker: (city) => `Désinfection · ${city}`,
        title: (city) => ["Désinfection professionnelle", `à ${city}`],
        metaTitle: (city) => `Désinfection professionnelle à ${city} | IGN Services Alsace`,
        metaDesc: (city, dept) =>
            `Désinfection professionnelle à ${city} (${dept}) : bureaux, commerces, locaux médicaux, copropriétés. Protocoles adaptés, produits professionnels. Devis gratuit sous 24h.`,
        heroImg: IMG.disinfect,
        intro: (city) => [
            `IGN Services Alsace propose des prestations de désinfection professionnelle à ${city} pour les bureaux, commerces, cabinets médicaux, copropriétés et tout type de local nécessitant un traitement sanitaire renforcé.`,
            `À ${city}, nos protocoles de désinfection utilisent des produits professionnels adaptés à chaque surface et à chaque situation. Notre personnel formé intervient avec rigueur pour restaurer un environnement sain et sécurisé.`,
        ],
        chips: ["Protocoles professionnels", "Produits certifiés", "Personnel formé", "Bureaux & commerces", "Locaux médicaux", "Copropriétés"],
        faq: (city) => [
            { q: `Proposez-vous la désinfection de bureaux à ${city} ?`, a: `Oui, nous réalisons la désinfection professionnelle de bureaux, commerces et locaux professionnels à ${city} avec des produits et protocoles adaptés.` },
            { q: `Vos produits sont-ils adaptés aux locaux médicaux à ${city} ?`, a: `Oui, nous utilisons des solutions professionnelles reconnues, adaptées aux environnements médicaux et aux exigences sanitaires les plus strictes.` },
            { q: `Pouvez-vous désinfecter une copropriété à ${city} ?`, a: `Oui, nous intervenons dans les copropriétés de ${city} pour la désinfection des parties communes, halls, ascenseurs et surfaces de contact.` },
        ],
        ctaLabel: "Obtenir un devis gratuit",
        parentLink: "/#services",
        parentLabel: "Nos services",
    },
};

// ── Cities to cross with services (main cities only to avoid thin content) ──
const CROSS_CITIES = [
    "strasbourg", "haguenau", "colmar", "schiltigheim", "illkirch",
    "lingolsheim", "bischheim", "ostwald", "obernai", "molsheim",
    "saverne", "selestat", "mulhouse", "erstein", "brumath",
    "bischwiller", "guebwiller", "thann", "cernay", "saint-louis",
    "altkirch", "rixheim", "wittenheim", "barr", "wissembourg",
];

// ── Generate all cross pages ──
export const SERVICE_LOCAL_PAGES = {};

CROSS_CITIES.forEach((cityKey) => {
    const cityData = LOCAL_PAGES[cityKey];
    if (!cityData) return;

    const { city } = cityData;
    const dept = cityData.metaDesc.match(/\((\d{2})\)/)?.[1] || "67";

    Object.entries(SERVICE_TEMPLATES).forEach(([svcKey, tpl]) => {
        // Skip Strasbourg for diogene/deces since dedicated pages exist
        if (cityKey === "strasbourg" && (svcKey === "diogene" || svcKey === "deces")) return;

        const pageKey = `${svcKey}-${cityKey}`;
        const slug = `${tpl.serviceSlug}-${cityKey.replace("_", "-")}`;

        SERVICE_LOCAL_PAGES[pageKey] = {
            slug,
            city,
            cityKey,
            serviceKey: svcKey,
            serviceLabel: tpl.serviceLabel,
            heroImg: tpl.heroImg,
            metaTitle: tpl.metaTitle(city),
            metaDesc: tpl.metaDesc(city, dept),
            kicker: tpl.kicker(city),
            title: tpl.title(city),
            intro: tpl.intro(city),
            chips: tpl.chips,
            faq: tpl.faq(city),
            ctaLabel: tpl.ctaLabel,
            parentLink: tpl.parentLink,
            parentLabel: tpl.parentLabel,
            serviceType: tpl.serviceType,
        };
    });
});
